import React, { useMemo, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, EmailOutlined, LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AuthSplitLayout from '../components/AuthSplitLayout';
import { getPostAuthDestination, sanitizeReturnTo } from '../utils/authRedirect';

export default function LoginPageEnhanced() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const returnTo = sanitizeReturnTo(searchParams.get('returnTo')) || sanitizeReturnTo(location.state?.returnTo);
  const reason = searchParams.get('reason') || location.state?.reason || '';
  const helperNotice = reason === 'session-expired'
    ? 'Your session expired while you were away. Sign in again and we will take you back where you left off.'
    : reason === 'password-updated'
      ? 'Your password was updated successfully. Sign in again to keep going.'
      : '';
  const authLinkSuffix = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      navigate(getPostAuthDestination(result.user, returnTo), { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      title={
        <>
          Better habits.
          <br />
          Better you.
        </>
      }
      subtitle="Sign in to continue your wellness journey with clear progress, calm tracking, and habits that stick."
      rightTitle="Welcome back"
      rightSubtitle="Sign in to your FastTime account"
      footer={
        <Typography variant="body2" sx={{ color: 'rgba(31,43,34,0.62)', textAlign: 'center' }}>
          Don&apos;t have an account?{' '}
          <Link to={`/signup${authLinkSuffix}`} style={{ color: '#b46f47', fontWeight: 600, textDecoration: 'none' }}>
            Get started
          </Link>
        </Typography>
      }
    >
      <Box sx={{ mb: 3 }}>
        <IconButton
          onClick={() => navigate('/')}
          sx={{ border: '1px solid rgba(31,43,34,0.14)', color: '#1f2b22' }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(230,124,115,0.08)', color: '#7b2f28' }}>
          {error}
        </Alert>
      ) : null}

      {!error && helperNotice ? (
        <Alert severity="warning" sx={{ mb: 3, bgcolor: 'rgba(180,111,71,0.1)', color: '#6f4126' }}>
          {helperNotice}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2.25}>
          <TextField
            fullWidth
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined sx={{ color: 'rgba(31,43,34,0.45)' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined sx={{ color: 'rgba(31,43,34,0.45)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((current) => !current)}>
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2.25, mb: 4 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                sx={{ color: 'rgba(31,43,34,0.34)' }}
              />
            }
            label={<Typography variant="body2" sx={{ color: 'rgba(31,43,34,0.68)' }}>Remember me</Typography>}
          />
          <Link to={`/forgot-password${authLinkSuffix}`} style={{ color: '#5f6f5d', fontWeight: 500, textDecoration: 'none' }}>
            Forgot password?
          </Link>
        </Stack>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            bgcolor: '#122019',
            color: '#f5efe4',
            py: 1.65,
            fontSize: '1rem',
            '&:hover': { bgcolor: '#1a2d24' },
          }}
        >
          {loading ? 'Signing in…' : 'Log in'}
        </Button>
      </Box>
    </AuthSplitLayout>
  );
}
